import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { KpiCard } from '@/components/app/KpiCard';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Home, Wrench, ClipboardList, MapPin, FileText, MessageSquare } from 'lucide-react';
import { PROPERTY_TYPE_LABEL } from '@/lib/constants';
import { Link } from 'react-router-dom';

export default function AgentDashboard() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/dashboard/agent').then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>;

  const k = data.kpi || {};
  return (
    <div className="space-y-5">
      <PageHeader title="Agent Dashboard" subtitle="Your assigned properties, available listings, and lead notes" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Assigned properties" value={k.assigned_properties || 0} icon={Building2} />
        <KpiCard label="Available units" value={k.vacant_units || 0} icon={Home} tone="success" />
        <KpiCard label="Occupied units" value={k.occupied_units || 0} icon={Home} />
        <KpiCard label="My notes" value={k.my_notes_count || 0} icon={MessageSquare} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 p-0 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-semibold text-sm flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Available listings</div>
            <span className="text-xs text-muted-foreground">{data.available_listings?.length || 0} vacant unit(s)</span>
          </div>
          <div className="divide-y">
            {(data.available_listings || []).length === 0 && (
              <div className="px-4 py-8 text-sm text-center text-muted-foreground">No vacant units available right now.</div>
            )}
            {(data.available_listings || []).map((l) => (
              <div key={l.unit_id} className="px-4 py-3 flex items-center gap-3 text-sm">
                <div className="h-10 w-14 rounded-md overflow-hidden bg-muted shrink-0">
                  {l.image_url ? <img src={l.image_url} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-5 w-5 m-auto mt-2.5 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{l.property_name} <span className="text-muted-foreground">· Unit {l.unit_number}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{PROPERTY_TYPE_LABEL[l.property_type] || l.property_type} · {l.configuration} · {l.area_sqft} sqft · Floor {l.floor || '—'} · {l.city}</div>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Vacant</Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b font-semibold text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Recent notes</span>
            <Link to="/agent/notes" className="text-xs text-[hsl(var(--role-accent))] hover:underline">View all →</Link>
          </div>
          <div className="divide-y">
            {(data.recent_notes || []).length === 0 && (
              <div className="px-4 py-8 text-sm text-center text-muted-foreground">No notes yet. Add visit or lead notes from a property page.</div>
            )}
            {(data.recent_notes || []).map((n) => (
              <div key={n.id} className="px-4 py-2.5 text-xs">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] capitalize">{n.note_type}</Badge>
                  {n.lead_status && <Badge variant="outline" className="text-[10px] capitalize bg-amber-50 text-amber-700 border-amber-200">{n.lead_status.replace(/_/g, ' ')}</Badge>}
                </div>
                <div className="mt-1 text-foreground line-clamp-2">{n.body}</div>
                <div className="text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b font-semibold text-sm flex items-center gap-2"><Building2 className="h-4 w-4" /> Your assigned properties</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
          {(data.properties || []).map((p) => (
            <Link key={p.id} to={`/agent/properties/${p.id}`} className="block">
              <div className="rounded-lg border overflow-hidden card-hover">
                <div className="h-28 bg-muted">
                  {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-muted-foreground"><Building2 className="h-7 w-7" /></div>}
                </div>
                <div className="p-3">
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{PROPERTY_TYPE_LABEL[p.type] || p.type}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> <span className="truncate">{p.city || '—'}</span></div>
                </div>
              </div>
            </Link>
          ))}
          {(data.properties || []).length === 0 && (
            <div className="col-span-full text-sm text-center text-muted-foreground py-8">No properties assigned to you yet. Ask your Super-Admin to assign listings.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
